import { useSelector } from 'react-redux';
import { useLocale } from '../contexts/LocaleContext';
import { formatHardwarePrice } from '../lib/hardwareFirst';
import { getLocalizedPricing, hasLocalizedPricing } from '../lib/util/money';
import { rescuePlan } from '../lib/rescueCheckout';
import * as types from '../types';
import useSubscriptionProducts from './useSubscriptionProducts';

/**
 * The activation fee on the plan an adopter is given, formatted for the active market ($20, CA$30),
 * or null when it cannot be known: products not loaded yet, no such plan, no price in this currency,
 * or no fee at all. Callers drop the amount rather than guess - a wrong figure in the one sentence
 * that promises the offer is worse than no figure.
 *
 * Resolves the same plan the rescue pdp preselects: the Mini's six-month, non-trial, first-party
 * plan on the side of the plus split the site defaults to.
 */
export default function useRescueActivationFee(): string | null {
  const plans = useSubscriptionProducts(types.miniCollarId);
  const miniPlusByDefault = useSelector((state: types.AppState) => !!state.config.siteConfig.miniPlusByDefault);
  const locale = useLocale();

  const fee = rescuePlan(plans, miniPlusByDefault)?.activationFeePricingInCents;

  if (!fee || !hasLocalizedPricing(fee, locale.currency)) {
    return null;
  }

  const feeInCents = getLocalizedPricing(fee, locale.currency);
  return feeInCents > 0 ? formatHardwarePrice(feeInCents, locale) : null;
}
